import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { busCheckoutService, CreateManualBusCheckoutDto } from '../services/busCheckoutService';
import { DateTimeInput, DateInput } from '../components';
import styles from './QuotePage.module.css';

interface PassengerForm {
  name: string;
  cpf: string;
  birthDate: string;
  rg: string;
  phone_number: string;
  seat_number: string;
}

interface TripForm {
  id: string;
  cidade_origem_slug: string;
  cidade_destino_slug: string;
  origem_slug: string;
  destino_slug: string;
  datetime_ida: string;
  chegada_ida: string;
  max_split_value: number;
  max_split_value_markup: number;
  company_name: string;
  tipo_assento: string;
  tipo_veiculo: string;
  vehicle_plate: string;
}

const emptyPassenger = (): PassengerForm => ({
  name: '', cpf: '', birthDate: '', rg: '', phone_number: '', seat_number: '',
});

const emptyTrip = (): TripForm => ({
  id: '', cidade_origem_slug: '', cidade_destino_slug: '',
  origem_slug: '', destino_slug: '', datetime_ida: '', chegada_ida: '',
  max_split_value: 0, max_split_value_markup: 0, company_name: '',
  tipo_assento: '', tipo_veiculo: '', vehicle_plate: '',
});

const BusManualCheckout: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [fonePayment, setFonePayment] = useState('');
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [reservedCode, setReservedCode] = useState('');
  const [roundTrip, setRoundTrip] = useState(false);

  const [tripIda, setTripIda] = useState<TripForm>(emptyTrip());
  const [tripVolta, setTripVolta] = useState<TripForm>(emptyTrip());
  const [passengers, setPassengers] = useState<PassengerForm[]>([emptyPassenger()]);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const updateTrip = (setter: React.Dispatch<React.SetStateAction<TripForm>>, field: keyof TripForm, value: string) =>
    setter(prev => ({ ...prev, [field]: value }));

  const updatePassenger = (i: number, field: keyof PassengerForm, value: string) =>
    setPassengers(prev => prev.map((p, idx) => idx === i ? { ...p, [field]: value } : p));

  const addPassenger = () => setPassengers(prev => [...prev, emptyPassenger()]);
  const removePassenger = (i: number) => setPassengers(prev => prev.filter((_, idx) => idx !== i));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(''); setSuccess(''); setSubmitting(true);

    try {
      const dto: CreateManualBusCheckoutDto = {
        email,
        fone_payment: fonePayment,
        total_value: (Number(tripIda?.max_split_value_markup) || 0) + (Number(tripVolta?.max_split_value_markup) || 0),
        name,
        notes: notes || `Checkout manual — operador: ${user?.username ?? ''}`,
        reserved_code_company: reservedCode || undefined,
        trip_ida_data: {
          id: tripIda.id,
          max_split_value_markup: Number(tripIda.max_split_value_markup) || 0,
          cidade_origem_slug: tripIda.cidade_origem_slug,
          cidade_destino_slug: tripIda.cidade_destino_slug,
          origem_slug: tripIda.origem_slug,
          destino_slug: tripIda.destino_slug,
          datetime_ida: tripIda.datetime_ida,
          chegada_ida: tripIda.chegada_ida,
          max_split_value: Number(tripIda.max_split_value) || 0,
          company_name: tripIda.company_name,
          tipo_assento: tripIda.tipo_assento,
          tipo_veiculo: tripIda.tipo_veiculo,
          vehicle_plate: tripIda.vehicle_plate || undefined,
        },
        passengers: passengers.map(p => ({
          name: p.name,
          cpf: p.cpf,
          birthDate: p.birthDate,
          rg: p.rg || undefined,
          phone_number: p.phone_number || undefined,
          seat_number: p.seat_number || undefined,
        })),
      };

      if (roundTrip && tripVolta.id) {
        dto.trip_volta_data = {
          id: tripVolta.id,
          max_split_value_markup: Number(tripVolta.max_split_value_markup) || 0,
          cidade_origem_slug: tripVolta.cidade_origem_slug,
          cidade_destino_slug: tripVolta.cidade_destino_slug,
          origem_slug: tripVolta.origem_slug,
          destino_slug: tripVolta.destino_slug,
          datetime_ida: tripVolta.datetime_ida,
          chegada_ida: tripVolta.chegada_ida,
          max_split_value: Number(tripVolta.max_split_value) || 0,
          company_name: tripVolta.company_name,
          tipo_assento: tripVolta.tipo_assento,
          tipo_veiculo: tripVolta.tipo_veiculo,
          vehicle_plate: tripVolta.vehicle_plate || undefined,
        };
      }

      await busCheckoutService.createManualCheckout(dto);
      setSuccess('Checkout de ônibus criado com sucesso!');
      setTimeout(() => navigate('/bus-emissions'), 2000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao criar checkout manual');
    } finally {
      setSubmitting(false);
    }
  };

  const renderTripFields = (trip: TripForm, setter: React.Dispatch<React.SetStateAction<TripForm>>, label: string) => (
    <div className={styles.card}>
      <h2 className={styles.sectionTitle}>{label}</h2>
      <div className={styles.formRow}>
        <div className={styles.inputGroup}>
          <label>ID da Viagem</label>
          <input value={trip.id} onChange={e => updateTrip(setter, 'id', e.target.value)} required />
        </div>
        <div className={styles.inputGroup}>
          <label>Empresa</label>
          <input value={trip.company_name} onChange={e => updateTrip(setter, 'company_name', e.target.value)} required />
        </div>
      </div>
      <div className={styles.formRow}>
        <div className={styles.inputGroup}>
          <label>Cidade Origem (slug)</label>
          <input value={trip.cidade_origem_slug} onChange={e => updateTrip(setter, 'cidade_origem_slug', e.target.value)} required placeholder="belo-horizonte-mg" />
        </div>
        <div className={styles.inputGroup}>
          <label>Cidade Destino (slug)</label>
          <input value={trip.cidade_destino_slug} onChange={e => updateTrip(setter, 'cidade_destino_slug', e.target.value)} required placeholder="sao-paulo-sp" />
        </div>
      </div>
      <div className={styles.formRow}>
        <div className={styles.inputGroup}>
          <label>Ponto Origem (slug)</label>
          <input value={trip.origem_slug} onChange={e => updateTrip(setter, 'origem_slug', e.target.value)} required placeholder="terminal-belo-horizonte-mg" />
        </div>
        <div className={styles.inputGroup}>
          <label>Ponto Destino (slug)</label>
          <input value={trip.destino_slug} onChange={e => updateTrip(setter, 'destino_slug', e.target.value)} required placeholder="barra-funda-sao-paulo-sp" />
        </div>
      </div>
      <div className={styles.formRow}>
        <div className={styles.inputGroup}>
          <label>Data/Hora Partida</label>
          <DateTimeInput value={trip.datetime_ida} onChange={v => updateTrip(setter, 'datetime_ida', v)} required />
        </div>
        <div className={styles.inputGroup}>
          <label>Data/Hora Chegada</label>
          <DateTimeInput value={trip.chegada_ida} onChange={v => updateTrip(setter, 'chegada_ida', v)} required />
        </div>
      </div>
      <div className={styles.formRow}>
        <div className={styles.inputGroup}>
          <label>Valor Base (R$)</label>
          <input type="number" step="0.01" value={trip.max_split_value} onChange={e => updateTrip(setter, 'max_split_value', e.target.value)} required />
        </div>
        <div className={styles.inputGroup}>
          <label>Valor c/ Markup (R$)</label>
          <input type="number" step="0.01" value={trip.max_split_value_markup} onChange={e => updateTrip(setter, 'max_split_value_markup', e.target.value)} required />
        </div>
      </div>
      <div className={styles.formRow}>
        <div className={styles.inputGroup}>
          <label>Tipo Assento</label>
          <input value={trip.tipo_assento} onChange={e => updateTrip(setter, 'tipo_assento', e.target.value)} required placeholder="leito individual" />
        </div>
        <div className={styles.inputGroup}>
          <label>Tipo Veículo</label>
          <input value={trip.tipo_veiculo} onChange={e => updateTrip(setter, 'tipo_veiculo', e.target.value)} required placeholder="DD" />
        </div>
        <div className={styles.inputGroup}>
          <label>Placa (opcional)</label>
          <input value={trip.vehicle_plate} onChange={e => updateTrip(setter, 'vehicle_plate', e.target.value)} placeholder="ABC-1234" />
        </div>
      </div>
    </div>
  );

  return (
    <div className={styles.page}>
      <h1 className={styles.pageTitle}>Checkout Manual de Ônibus</h1>

      <form onSubmit={handleSubmit}>
        {/* Dados do cliente */}
        <div className={styles.card}>
          <h2 className={styles.sectionTitle}>1. Dados do Cliente</h2>
          <div className={styles.formRow}>
            <div className={styles.inputGroup}>
              <label>Nome Completo</label>
              <input value={name} onChange={e => setName(e.target.value)} required />
            </div>
            <div className={styles.inputGroup}>
              <label>Email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} required />
            </div>
            <div className={styles.inputGroup}>
              <label>Telefone</label>
              <input value={fonePayment} onChange={e => setFonePayment(e.target.value)} required placeholder="31999999999" />
            </div>
          </div>
          <div className={styles.formRow}>
            <div className={styles.inputGroup}>
              <label>Cód. Reserva Empresa</label>
              <input value={reservedCode} onChange={e => setReservedCode(e.target.value)} placeholder="Opcional" />
            </div>
            <div className={styles.inputGroup}>
              <label>Observação</label>
              <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Nota interna do operador" />
            </div>
          </div>
          <label className={styles.checkRow}>
            <input type="checkbox" checked={roundTrip} onChange={e => setRoundTrip(e.target.checked)} />
            Ida e Volta
          </label>
        </div>

        {/* Viagem de ida */}
        {renderTripFields(tripIda, setTripIda, '2. Viagem de Ida')}

        {/* Viagem de volta */}
        {roundTrip && renderTripFields(tripVolta, setTripVolta, '3. Viagem de Volta')}

        {/* Passageiros */}
        <div className={styles.card}>
          <h2 className={styles.sectionTitle}>{roundTrip ? '4' : '3'}. Passageiros</h2>
          {passengers.map((p, i) => (
            <div key={i} className={styles.passengerCard}>
              <p className={styles.passengerTitle}>
                Passageiro {i + 1}
                {passengers.length > 1 && (
                  <button type="button" onClick={() => removePassenger(i)}
                    style={{ marginLeft: 12, color: '#c62828', background: 'none', border: 'none', cursor: 'pointer', fontSize: 12 }}>
                    ✕ Remover
                  </button>
                )}
              </p>
              <div className={styles.formRow}>
                <div className={styles.inputGroup}>
                  <label>Nome Completo</label>
                  <input value={p.name} onChange={e => updatePassenger(i, 'name', e.target.value)} required />
                </div>
                <div className={styles.inputGroup}>
                  <label>CPF</label>
                  <input value={p.cpf} onChange={e => updatePassenger(i, 'cpf', e.target.value)} required placeholder="123.456.789-00" />
                </div>
              </div>
              <div className={styles.formRow}>
                <div className={styles.inputGroup}>
                  <label>Data Nascimento</label>
                  <DateInput value={p.birthDate} onChange={v => updatePassenger(i, 'birthDate', v)} required />
                </div>
                <div className={styles.inputGroup}>
                  <label>RG (opcional)</label>
                  <input value={p.rg} onChange={e => updatePassenger(i, 'rg', e.target.value)} />
                </div>
                <div className={styles.inputGroup}>
                  <label>Telefone (opcional)</label>
                  <input value={p.phone_number} onChange={e => updatePassenger(i, 'phone_number', e.target.value)} />
                </div>
                <div className={styles.inputGroup} style={{ flex: '0 1 100px' }}>
                  <label>Assento</label>
                  <input value={p.seat_number} onChange={e => updatePassenger(i, 'seat_number', e.target.value)} placeholder="42" />
                </div>
              </div>
            </div>
          ))}
          <button type="button" className={styles.btnSecondary} onClick={addPassenger}>
            + Adicionar Passageiro
          </button>
        </div>

        {/* Ações */}
        <div className={styles.btnRow}>
          <button type="submit" className={styles.btnPrimary} disabled={submitting}>
            {submitting ? 'Criando...' : '🚌 Criar Checkout Manual'}
          </button>
        </div>

        {error && <p className={styles.error}>{error}</p>}
        {success && (
          <div className={styles.linkBox}>
            <p className={styles.linkBoxTitle}>✅ {success}</p>
          </div>
        )}
      </form>
    </div>
  );
};

export default BusManualCheckout;
